import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react"
import {ANALYTICS_API_URL} from "../../utils/api"
import {applyAdminRequestHeaders} from "../../utils/apiHeaders"
import type {StoreState} from "../store"
import type {ReportCatalog, ReportPreview, ReportRequest} from "./reportTypes"

const rawBase = fetchBaseQuery({
    baseUrl: ANALYTICS_API_URL,
    prepareHeaders: (headers, {getState}) => {
        const token = (getState() as StoreState).auth?.token
        if (token) headers.set("Authorization", `Bearer ${token}`)
        return applyAdminRequestHeaders(headers)
    }
})
const baseQuery: typeof rawBase = async (args, api, options) => {
    if (!ANALYTICS_API_URL) return {error: {status: "CUSTOM_ERROR", error: "NOT_CONFIGURED"}}
    const controller = new AbortController()
    const abort = () => controller.abort()
    if (api.signal.aborted) abort()
    else api.signal.addEventListener("abort", abort, {once: true})
    let timedOut = false
    const timer = setTimeout(() => {timedOut = true; abort()}, 35_000)
    try {
        const result = await rawBase(args, {...api, signal: controller.signal}, options)
        return timedOut ? {error: {status: "TIMEOUT_ERROR", error: "EXPORT_TIMEOUT"}} : result
    }
    finally { clearTimeout(timer); api.signal.removeEventListener("abort", abort) }
}
export const reportApi = createApi({
    reducerPath: "reportApi",
    baseQuery,
    keepUnusedDataFor: 0,
    endpoints: build => ({
        getReportCatalog: build.query<ReportCatalog, string>({query: () => "v1/reports/catalog"}),
        previewReport: build.mutation<ReportPreview, ReportRequest>({
            query: body => ({url: "v1/reports/preview", method: "POST", body})
        })
    })
})
export const {useGetReportCatalogQuery, usePreviewReportMutation} = reportApi
